import { Box } from "@otrium/core";
import GooglePayButton from "@google-pay/button-react";
import { FC, useCallback, useContext, useEffect, useState } from "react";
import getGooglePaySettings from "src/molecules/ExpressCheckout/getGooglePaySettings";
import { formatStandardPrice } from "src/utils/ct/priceHelper";
import { Cart } from "src/types/ctgraphql.d";
import { AppCtx } from "src/contexts/app.context";
import { PaymentMethod } from "src/types/graphql.d";
import { useSegment } from "src/hooks/useSegment";

export interface GooglePayUserData {
  shippingAddress?: google.payments.api.Address;
  billingAddress?: google.payments.api.Address;
  customerEmail?: string;
  payment_method: PaymentMethod;
  token: string;
}

interface GooglePayProps {
  cart: Cart;
  onPaymentReady: (userData: GooglePayUserData) => void;
  onError: (error?: Error) => void;
}

const environment =
  (process.env.GOOGLE_PAY_ENVIRONMENT as google.payments.api.Environment) ||
  "TEST";

const GooglePay: FC<GooglePayProps> = ({ cart, onPaymentReady, onError }) => {
  const { trackingGooglePayButtonClicked } = useSegment();
  const { storeConfig, adyenMerchantId } = useContext(AppCtx);
  const [googlePayRequestData, setGooglePayRequestData] =
    useState<google.payments.api.PaymentDataRequest>();
  useEffect(() => {
    if (cart) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const paymentRequest = getGooglePaySettings({
          transactionInfo: {
            totalPriceStatus: "FINAL",
            totalPriceLabel: "Total",
            totalPrice: formatStandardPrice(
              cart.taxedPrice?.totalGross || cart.totalPrice
            ).toString(),
            currencyCode: cart.totalPrice.currencyCode as string,
            countryCode: cart.country as string,
          },
          countryCode: cart.country as string,
          adyenMerchantId,
        });
        setGooglePayRequestData(paymentRequest);
      } catch (e) {
        console.error("Cannot set up google pay form", e);
      }
    } else {
      console.error("Cart data is not available");
    }
  }, [cart]);

  const handleLoadPaymentData = useCallback(
    (paymentRequest: google.payments.api.PaymentData) => {
      onPaymentReady({
        shippingAddress: paymentRequest.shippingAddress,
        billingAddress: paymentRequest.paymentMethodData.info?.billingAddress,
        customerEmail: paymentRequest.email,
        payment_method: PaymentMethod.GooglePay,
        token: paymentRequest.paymentMethodData.tokenizationData.token,
      });
    },
    [onPaymentReady]
  );

  const handleGooglePayError = useCallback(
    (error: google.payments.api.PaymentsError | Error) => {
      const googlePayError =
        "statusCode" in error
          ? new Error(
              `GooglePayError ${error.statusCode}: ${error.statusMessage}`
            )
          : error;
      if (window.NREUM) {
        window.NREUM.noticeError(googlePayError);
      }
      console.error(googlePayError);
      onError(googlePayError);
    },
    [onError]
  );

  return googlePayRequestData ? (
    <Box
      mt={[2, 2, 2, 0]}
      ml={[0, 0, 0, "24px"]}
      width={["auto", "auto", "auto", "50%"]}
      height={48}
    >
      <GooglePayButton
        environment={environment}
        paymentRequest={googlePayRequestData}
        onLoadPaymentData={handleLoadPaymentData}
        onError={handleGooglePayError}
        onClick={() => {
          void trackingGooglePayButtonClicked(cart.id);
        }}
        buttonLocale={storeConfig.countryCode?.toLowerCase() || "en"}
        buttonSizeMode="fill"
        style={{ height: "inherit", width: "100%" }}
      />
    </Box>
  ) : (
    <></>
  );
};

export default GooglePay;
